import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../contexts/LanguageContext';
import { pickLocalizedText } from '../utils/localizedContent';

// Métodos de pago asíncronos / 3DS / retraso del webhook pueden superar unos segundos.
// Limitar reintentos para que el usuario no espere minutos en el spinner.
const MAX_POLL_ATTEMPTS = 24;
const POLL_INTERVAL_MS = 5000;
const POLL_INTERVAL_AFTER_503_MS = 8000;
const INITIAL_POLL_DELAY_MS = 400;
const AXIOS_TIMEOUT_MS = 28000;
const MAX_503_STREAK = 6;

function isPaidStatus(payment_status) {
  return payment_status === 'paid' || payment_status === 'no_payment_required';
}

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const session_id = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [transaction, setTransaction] = useState(null);
  const { clearCart } = useCart();
  const { lang } = useLang();
  const text = (es, en) => pickLocalizedText(lang, es, en);

  const poll = useCallback(async (attempts = 0, streak503 = 0) => {
    if (!session_id) return;
    if (!API) {
      setStatus('error');
      return;
    }
    if (attempts === 0) {
      await new Promise((r) => setTimeout(r, INITIAL_POLL_DELAY_MS));
    }
    let nextDelay = POLL_INTERVAL_MS;
    let nextStreak503 = 0;
    try {
      const { data } = await axios.get(`${API}/payments/status-public/${session_id}`, {
        timeout: AXIOS_TIMEOUT_MS,
      });
      setTransaction(data);
      if (isPaidStatus(data.payment_status)) {
        setStatus('success');
        clearCart();
        return;
      }
      const stripeExpired = data.stripe_checkout_status === 'expired' || data.status === 'expired';
      if (stripeExpired || attempts >= MAX_POLL_ATTEMPTS) {
        setStatus(stripeExpired ? 'error' : 'uncertain');
        return;
      }
    } catch (err) {
      const code = err.response?.status;
      if (code === 503) {
        nextDelay = POLL_INTERVAL_AFTER_503_MS;
        nextStreak503 = streak503 + 1;
        if (nextStreak503 >= MAX_503_STREAK) {
          setStatus('uncertain');
          return;
        }
      }
      if (attempts >= MAX_POLL_ATTEMPTS) {
        setStatus('uncertain');
        return;
      }
    }
    setTimeout(() => poll(attempts + 1, nextStreak503), nextDelay);
  }, [session_id, clearCart]);

  useEffect(() => {
    if (!session_id) {
      setStatus('error');
      return;
    }
    if (!API) {
      setStatus('error');
      return;
    }
    poll();
  }, [session_id, poll]);

  const processing =
    transaction?.payment_intent_status === 'processing' ||
    transaction?.payment_intent_status === 'requires_action';

  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center page-enter">
      {status === 'loading' && (
        <>
          <div className="w-16 h-16 border-2 border-[#10B981]/30 border-t-[#10B981] rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-bold text-white">{text('Verificando pago...', 'Verifying payment...')}</h2>
          {processing && (
            <p className="text-[#A1A1AA] text-sm mt-4">
              {text(
                'Stripe sigue procesando el pago (banco, 3D Secure…). Puede tardar un minuto.',
                'Stripe is still processing your payment (bank, 3D Secure…). This can take a minute.',
              )}
            </p>
          )}
        </>
      )}
      {status === 'success' && (
        <>
          <CheckCircle size={64} className="text-[#10B981] mx-auto mb-6" />
          <h2 className="text-3xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {text('¡Pago exitoso!', 'Payment successful!')}
          </h2>
          <p className="text-[#A1A1AA] mb-4">
            {text('Tu pedido ha sido procesado correctamente.', 'Your order has been processed successfully.')}
          </p>
          {transaction && <p className="text-[#10B981] font-bold text-xl mb-8">{transaction.amount?.toFixed(2)}€</p>}
          <div className="flex flex-col gap-3">
            <Link to="/profile" className="btn-primary py-3 inline-block" data-testid="view-orders-btn">
              {text('Ver mis pedidos', 'View my orders')}
            </Link>
            <Link to="/store" className="btn-secondary py-3 inline-block" data-testid="continue-shopping-btn">
              {text('Seguir comprando', 'Continue shopping')}
            </Link>
          </div>
        </>
      )}
      {status === 'uncertain' && (
        <>
          <AlertCircle size={64} className="text-[#F59E0B] mx-auto mb-6" />
          <h2 className="text-3xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {text('Confirmación pendiente', 'Confirmation pending')}
          </h2>
          <p className="text-[#A1A1AA] mb-4">
            {text(
              'No pudimos confirmar el pago al instante. Si ves un cargo en tu cuenta, el pago puede estar procesándose.',
              'We could not confirm your payment immediately. If you see a charge on your bank statement, it may still be processing.',
            )}
          </p>
          <p className="text-[#A1A1AA] mb-8 text-sm">
            {text(
              'Revisa «Mis pedidos» en unos minutos o contacta con soporte con el comprobante de Stripe.',
              'Check “My orders” in a few minutes or contact support with your Stripe receipt.',
            )}
          </p>
          <div className="flex flex-col gap-3">
            <Link to="/profile" className="btn-primary py-3 inline-block">
              {text('Ver mis pedidos', 'View my orders')}
            </Link>
            <Link to="/store" className="btn-secondary py-3 inline-block">
              {text('Seguir comprando', 'Continue shopping')}
            </Link>
          </div>
        </>
      )}
      {status === 'error' && (
        <>
          <XCircle size={64} className="text-[#EF4444] mx-auto mb-6" />
          <h2 className="text-3xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {text('Pago no completado', 'Payment not completed')}
          </h2>
          <p className="text-[#A1A1AA] mb-8">
            {text('El pago no pudo completarse o expiró.', 'The payment could not be completed or expired.')}
          </p>
          <Link to="/cart" className="btn-primary py-3 inline-block" data-testid="retry-btn">
            {text('Intentar de nuevo', 'Try again')}
          </Link>
        </>
      )}
    </div>
  );
}
